import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Grid,
  Alert,
  Chip,
  MenuItem,
  Collapse,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Card,
  CardContent,
  Stack,
  Fab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Edit, Delete, ExpandMore, ExpandLess, Email, Phone, Person, Star, StarBorder } from '@mui/icons-material';
import { useConfirmDialog } from './ConfirmDialog';
import { useTerms } from '../context/TerminologyContext';
import {
  getAffiliates, createAffiliate, updateAffiliate, deleteAffiliate,
  getAffiliateContacts, createAffiliateContact, updateContact, deleteContact,
  getCompanies,
} from '../api';

export default function Affiliates() {
  const { t } = useTerms();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();

  const [affiliates, setAffiliates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAffiliate, setCurrentAffiliate] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Contact management
  const [expandedAffiliate, setExpandedAffiliate] = useState(null);
  const [contacts, setContacts] = useState({});
  const [openContactDialog, setOpenContactDialog] = useState(false);
  const [editContactMode, setEditContactMode] = useState(false);
  const [currentContact, setCurrentContact] = useState(null);
  const [contactAffiliateId, setContactAffiliateId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    // Required — every affiliate belongs to exactly one client. The Add
    // button is disabled until a client is picked.
    company_id: '',
  });

  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    is_primary: false
  });

  useEffect(() => {
    loadAffiliates();
    loadCompanies();
  }, []);

  const loadAffiliates = async () => {
    try {
      const res = await getAffiliates();
      setAffiliates(res.data);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    }
  };

  const loadCompanies = async () => {
    try {
      const res = await getCompanies();
      setCompanies(res.data);
    } catch (err) {
      // Non-fatal — the Client column just won't resolve names. Still useful.
      console.error(err);
    }
  };

  const companyName = (companyId) =>
    companyId == null ? null : companies.find(c => c.id === companyId)?.name;

  const loadContacts = async (affiliateId) => {
    try {
      const res = await getAffiliateContacts(affiliateId);
      setContacts(prev => ({ ...prev, [affiliateId]: res.data }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleExpand = (affiliateId) => {
    if (expandedAffiliate === affiliateId) {
      setExpandedAffiliate(null);
    } else {
      setExpandedAffiliate(affiliateId);
      if (!contacts[affiliateId]) {
        loadContacts(affiliateId);
      }
    }
  };

  const handleOpenDialog = (affiliate = null) => {
    if (affiliate) {
      setEditMode(true);
      setCurrentAffiliate(affiliate);
      setFormData({
        name: affiliate.name,
        description: affiliate.description || '',
        company_id: affiliate.company_id || '',
      });
    } else {
      setEditMode(false);
      setCurrentAffiliate(null);
      setFormData({
        name: '',
        description: '',
        company_id: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (editMode) {
        await updateAffiliate(currentAffiliate.id, formData);
        setSuccess(`${t('affiliate.singular')} updated successfully`);
      } else {
        await createAffiliate(formData);
        setSuccess(`${t('affiliate.singular')} created successfully`);
      }
      handleCloseDialog();
      loadAffiliates();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to save ${t('affiliate.singular', { lower: true })}`);
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm({
      title: `Delete ${t('affiliate.singular')}`,
      message: `Are you sure you want to delete this ${t('affiliate.singular', { lower: true })}? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning'
    });
    if (!confirmed) return;
    try {
      await deleteAffiliate(id);
      setSuccess(`${t('affiliate.singular')} deleted successfully`);
      loadAffiliates();
    } catch (err) {
      setError(`Failed to delete ${t('affiliate.singular', { lower: true })}`);
      console.error(err);
    }
  };

  // Contact handlers
  const handleOpenContactDialog = (affiliateId, contact = null) => {
    setContactAffiliateId(affiliateId);
    if (contact) {
      setEditContactMode(true);
      setCurrentContact(contact);
      setContactFormData({
        name: contact.name,
        email: contact.email,
        phone: contact.phone || '',
        role: contact.role || '',
        is_primary: contact.is_primary || false
      });
    } else {
      setEditContactMode(false);
      setCurrentContact(null);
      setContactFormData({
        name: '',
        email: '',
        phone: '',
        role: '',
        is_primary: false
      });
    }
    setOpenContactDialog(true);
  };

  const handleCloseContactDialog = () => {
    setOpenContactDialog(false);
  };

  const handleContactInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContactFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleContactSubmit = async () => {
    try {
      if (editContactMode) {
        await updateContact(currentContact.id, contactFormData);
        setSuccess('Contact updated successfully');
      } else {
        await createAffiliateContact(contactAffiliateId, contactFormData);
        setSuccess('Contact added successfully');
      }
      handleCloseContactDialog();
      loadContacts(contactAffiliateId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save contact');
      console.error(err);
    }
  };

  const handleDeleteContact = async (affiliateId, contactId) => {
    const confirmed = await showConfirm({
      title: 'Delete Contact',
      message: 'Are you sure you want to delete this contact?',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning'
    });
    if (!confirmed) return;
    try {
      await deleteContact(contactId);
      setSuccess('Contact deleted successfully');
      loadContacts(affiliateId);
    } catch (err) {
      setError('Failed to delete contact');
      console.error(err);
    }
  };

  // Mobile Contact Card
  const renderMobileContact = (affiliate, contact) => (
    <Card key={contact.id} variant="outlined" sx={{ mb: 1 }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2">{contact.name}</Typography>
              {contact.is_primary && <Star sx={{ color: 'warning.main', fontSize: 16 }} />}
            </Box>
            {contact.role && (
              <Typography variant="caption" color="text.secondary">{contact.role}</Typography>
            )}
          </Box>
          <Box>
            <IconButton size="small" onClick={() => handleOpenContactDialog(affiliate.id, contact)}>
              <Edit fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => handleDeleteContact(affiliate.id, contact.id)}>
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ mt: 1 }}>
          {contact.email && (
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
              <a href={`mailto:${contact.email}`} style={{ fontSize: '0.875rem' }}>{contact.email}</a>
            </Typography>
          )}
          {contact.phone && (
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
              <a href={`tel:${contact.phone}`} style={{ fontSize: '0.875rem' }}>{contact.phone}</a>
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  // Mobile Card View
  const renderMobileView = () => (
    <Stack spacing={1.5}>
      {affiliates.map(affiliate => (
        <Card key={affiliate.id} variant="outlined">
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            {/* Header with name and actions */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {affiliate.name}
                  </Typography>
                  {affiliate.company_id
                    ? <Chip size="small" label={companyName(affiliate.company_id) || `#${affiliate.company_id}`} variant="outlined" />
                    : <Chip size="small" label="Tenant-wide" variant="outlined" />}
                </Stack>
                {affiliate.description && (
                  <Typography variant="body2" color="text.secondary">
                    {affiliate.description}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton size="small" onClick={() => handleOpenDialog(affiliate)}>
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(affiliate.id)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Contacts section */}
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5, mt: 1 }}>
              <Button
                size="small"
                fullWidth
                variant="outlined"
                onClick={() => handleToggleExpand(affiliate.id)}
                endIcon={expandedAffiliate === affiliate.id ? <ExpandLess /> : <ExpandMore />}
              >
                {contacts[affiliate.id]?.length > 0 
                  ? `${contacts[affiliate.id].length} Contact(s)` 
                  : 'View Contacts'}
              </Button>
              
              <Collapse in={expandedAffiliate === affiliate.id} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Contacts</Typography>
                    <Button size="small" startIcon={<Add />} onClick={() => handleOpenContactDialog(affiliate.id)}>
                      Add
                    </Button>
                  </Box>
                  {contacts[affiliate.id]?.length > 0 ? (
                    contacts[affiliate.id].map(contact => renderMobileContact(affiliate, contact))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No contacts added yet
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          </CardContent>
        </Card>
      ))}
      {affiliates.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">No {t('affiliate.plural', { lower: true })} found</Typography>
        </Box>
      )}
    </Stack>
  );

  // Desktop Table View
  const renderDesktopView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width={50}></TableCell>
            <TableCell>Name</TableCell>
            <TableCell>{t('client.singular')}</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Contacts</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {affiliates.map(affiliate => (
            <React.Fragment key={affiliate.id}>
              <TableRow>
                <TableCell>
                  <IconButton size="small" onClick={() => handleToggleExpand(affiliate.id)}>
                    {expandedAffiliate === affiliate.id ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </TableCell>
                <TableCell>{affiliate.name}</TableCell>
                <TableCell>
                  {affiliate.company_id
                    ? <Chip size="small" label={companyName(affiliate.company_id) || `#${affiliate.company_id}`} variant="outlined" />
                    : <Chip size="small" label="Tenant-wide" variant="outlined" color="default" />}
                </TableCell>
                <TableCell>{affiliate.description || 'N/A'}</TableCell>
                <TableCell>
                  {contacts[affiliate.id]?.length > 0 ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleToggleExpand(affiliate.id)}
                      endIcon={expandedAffiliate === affiliate.id ? <ExpandLess /> : <ExpandMore />}
                    >
                      {contacts[affiliate.id].length} contact(s)
                    </Button>
                  ) : expandedAffiliate === affiliate.id ? (
                    <Chip size="small" label="No contacts" variant="outlined" />
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleToggleExpand(affiliate.id)}
                      endIcon={<ExpandMore />}
                    >
                      View Contacts
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title={`Edit ${t('affiliate.singular')}`}>
                    <IconButton size="small" onClick={() => handleOpenDialog(affiliate)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={`Delete ${t('affiliate.singular')}`}>
                    <IconButton size="small" onClick={() => handleDelete(affiliate.id)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={6} sx={{ p: 0, borderBottom: expandedAffiliate === affiliate.id ? 1 : 0 }}>
                  <Collapse in={expandedAffiliate === affiliate.id} timeout="auto" unmountOnExit>
                    <Box sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                          <Email sx={{ mr: 1 }} /> Contacts
                        </Typography>
                        <Button 
                          size="small" 
                          startIcon={<Add />} 
                          onClick={() => handleOpenContactDialog(affiliate.id)}
                        >
                          Add Contact
                        </Button>
                      </Box>
                      {contacts[affiliate.id]?.length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Email</TableCell>
                              <TableCell>Phone</TableCell>
                              <TableCell>Role</TableCell>
                              <TableCell>Primary</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {contacts[affiliate.id].map(contact => (
                              <TableRow key={contact.id}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Person sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                                    {contact.name}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Email sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                                    <a href={`mailto:${contact.email}`}>{contact.email}</a>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  {contact.phone && (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Phone sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                                      {contact.phone}
                                    </Box>
                                  )}
                                </TableCell>
                                <TableCell>{contact.role || '-'}</TableCell>
                                <TableCell>
                                  {contact.is_primary ? (
                                    <Star sx={{ color: 'warning.main' }} />
                                  ) : (
                                    <StarBorder sx={{ color: 'text.disabled' }} />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <IconButton size="small" onClick={() => handleOpenContactDialog(affiliate.id, contact)}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" onClick={() => handleDeleteContact(affiliate.id, contact.id)}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                          No contacts added yet
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ pb: isMobile ? 10 : 0 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">{t('affiliate.plural')}</Typography>
        {!isMobile && (
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
            {`Add ${t('affiliate.singular')}`}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {isMobile ? renderMobileView() : renderDesktopView()}

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label={`add ${t('affiliate.singular', { lower: true })}`}
          onClick={() => handleOpenDialog()}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            zIndex: 1000,
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Affiliate Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        aria-labelledby="affiliate-dialog-title"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      >
        <DialogTitle id="affiliate-dialog-title">{editMode ? `Edit ${t('affiliate.singular')}` : `Add New ${t('affiliate.singular')}`}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label={`${t('affiliate.singular')} Name`}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth select required
                label={t('client.singular')}
                name="company_id"
                value={formData.company_id}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                size={isMobile ? 'small' : 'medium'}
                helperText={`${t('affiliate.plural')} are sub-organizations of one specific ${t('client.singular', { lower: true })} and appear in the ${t('affiliate.singular', { lower: true })} dropdown only when that ${t('client.singular', { lower: true })} is picked on a time entry.`}
              >
                {companies.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
          <Button onClick={handleCloseDialog} size={isMobile ? 'large' : 'medium'}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!formData.name || !formData.company_id} size={isMobile ? 'large' : 'medium'}>
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog
        open={openContactDialog}
        onClose={handleCloseContactDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        aria-labelledby="affiliate-contact-dialog-title"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleContactSubmit();
          }
        }}
      >
        <DialogTitle id="affiliate-contact-dialog-title">{editContactMode ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Contact Name"
                name="name"
                value={contactFormData.name}
                onChange={handleContactInputChange}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                name="email"
                value={contactFormData.email}
                onChange={handleContactInputChange}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={contactFormData.phone}
                onChange={handleContactInputChange}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Role/Title"
                name="role"
                value={contactFormData.role}
                onChange={handleContactInputChange}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={contactFormData.is_primary}
                    onChange={handleContactInputChange}
                    name="is_primary"
                  />
                }
                label="Primary Contact"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
          <Button onClick={handleCloseContactDialog} size={isMobile ? 'large' : 'medium'}>Cancel</Button>
          <Button variant="contained" onClick={handleContactSubmit} size={isMobile ? 'large' : 'medium'}>
            {editContactMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialogComponent />
    </Box>
  );
}
